import React, { useCallback, useReducer, useEffect, useState, useMemo } from 'react';
import { Divider, Header, Icon, Form, Input, Dropdown, Modal } from 'semantic-ui-react';
import request from 'services/http';
import { tap, distinctUntilChanged, debounceTime, switchMap, finalize, map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { maskCurrency, paramsToSearchURL } from 'utils';
import MaskedInput from 'react-text-mask';
import history from 'services/history';
import { DateTime } from 'luxon';
import { numParse } from '@rededompedro/utils';
import { toast } from 'react-toastify';

const initialValues = {
    data_cupom_ini: '',
    data_cupom_fim: '',
    unidade: '',
    forma_pgto: '',
    numero_cupom: '',
    cliente: '',
    valor_cupom: '',
    data_fatura_ini: '',
    data_fatura_fim: '',
    numero_fatura: '',
    valor_fatura: '',
};

function filterReducer(state, action) {
    if (action.type in state) {
        const { type, payload } = action;
        return { ...state, [type]: payload };
    }
}

export default function FilterModal(props) {
    const { open, onClose } = props;
    const [filter, setFilter] = useReducer(filterReducer, initialValues);
    const [companies, setCompanies] = useState([]);
    const [customers, setCustomers] = useState([]);

    useEffect(() => {
        const empresas$ = request({ url: '/empresas' })
            .pipe(
                tap(({ response }) => setCompanies(response.empresas)),
                finalize(() => empresas$.unsubscribe()),
            ).subscribe();
    }, []);

    const searchCustomer$ = useMemo(() => {
        const subject$ = new Subject()
            .pipe(
                distinctUntilChanged(),
                debounceTime(800),
                switchMap((data) => request({
                    url: '/common/customers',
                    params: { search: data },
                })),
                map(({ response }) => {
                    setCustomers(response);
                }),
            );
        subject$.subscribe();
        return subject$;
    }, []);

    const onFieldChange = useCallback((event, data) => {
        const name = data?.name || event.target.name;
        let value = data?.value || event.target.value;
        if (name && value) {
            switch (name) {
                case 'valor_cupom':
                case 'valor_fatura':
                    value = maskCurrency(value);
                break;

                default:
                    
            }
            setFilter({ type: name, payload: value });
        }
    }, []);

    const submit = useCallback(() => {
        const params = {};

        if (!Boolean(filter['unidade'])) {
            toast.error('O campo unidade é obrigatório.');
            return;
        }

        for (const param in filter) {
            if (filter[param]) {
                switch (param) {
                    case 'data_fatura_ini':
                    case 'data_fatura_fim':
                        if (!params.data_fatura) {
                            params.data_fatura = [];
                        }
                        params.data_fatura[param === 'data_fatura_ini' ? 0 : 1] = DateTime.fromFormat(filter[param], 'dd/MM/yyyy').toISODate();
                        break;

                    case 'data_cupom_ini':
                    case 'data_cupom_fim':
                        if (!params.data_cupom) {
                            params.data_cupom = [];
                        }
                        params.data_cupom[param === 'data_cupom_ini' ? 0 : 1] = DateTime.fromFormat(filter[param], 'dd/MM/yyyy').toISODate();
                        break;

                    case 'valor_fatura':
                    case 'valor_cupom':
                        params[param] = numParse(filter[param]);
                        break;

                    default:
                        params[param] = filter[param];
                }
            }
        }

        const queryString = paramsToSearchURL(params);
        history.push({
            search: queryString,
            pathname: 'conferencia-de-cupons/detalhes'
        });
    }, [filter]);

    return (
        <Modal open={open} onClose={onClose}>
            <Modal.Content>
                <Divider id="divider-cupom" horizontal>
                    <Header as="h4">
                        <Icon name="file alternate outline" />
                        Cupom
                    </Header>
                </Divider>
                <Form onSubmit={submit}>
                    <Form.Group>
                        <Form.Field className="period">
                            <label>Data</label>
                            <MaskedInput
                                style={{ marginRight: 8 }}
                                className="masked-input"
                                mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                name="data_cupom_ini"
                                showMask={true}
                                value={filter.data_cupom_ini}
                                onChange={onFieldChange}
                            />
                            <MaskedInput
                                className="masked-input"
                                mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                name="data_cupom_fim"
                                showMask={true}
                                value={filter.data_cupom_fim}
                                onChange={onFieldChange}
                            />
                        </Form.Field>
                        <Form.Select
                            search
                            required
                            width={6}
                            selectOnBlur={false}
                            name="unidade"
                            label='Unidade'
                            value={filter.unidade}
                            onChange={onFieldChange}
                            options={companies.map((it) => ({
                                key: it._id,
                                value: it._id,
                                text: it.nomeEmpresa
                            }))}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Select
                            search
                            multiple
                            style={{ minWidth: 150 }}
                            selectOnBlur={false}
                            name="forma_pgto"
                            label='Forma Pgto.'
                            value={filter.forma_pgto}
                            onChange={onFieldChange}
                            options={[
                                { key: 177, value: 177, text: 'Carta Frete' },
                                { key: 9611, value: 9611, text: 'CTF' },
                                { key: 163, value: 163, text: 'Nota a Prazo' },
                                { key: 53931954, value: 53931954, text: 'Pró Frotas' },
                                { key: 47357, value: 47357, text: 'Vale' },
                            ]}
                        />
                        <Form.Input
                            name="numero_cupom"
                            label='Número'
                            value={filter.numero_cupom}
                            onChange={onFieldChange}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Select
                            search
                            name="cliente"
                            selectOnBlur={false}
                            label='Cliente'
                            width={8}
                            // value={filter.cliente}
                            onChange={onFieldChange}
                            onSearchChange={(_, { searchQuery }) => Boolean(searchQuery) ? searchCustomer$.next(searchQuery) : undefined}
                            noResultsMessage='Sem resultados.'
                            options={customers.map((it) => ({
                                key: it.id,
                                value: it.id,
                                text: it.nome,
                            }))}
                        />
                        <Form.Field>
                            <label>Valor</label>
                            <Input
                                name="valor_cupom"
                                value={filter.valor_cupom}
                                onChange={onFieldChange}
                                label={<Dropdown defaultValue='=' options={[
                                    { key: '=', text: '=', value: '=' },
                                    { key: '>', text: '>', value: '>' },
                                    { key: '<', text: '<', value: '<' },
                                ]} />}
                                labelPosition='left'
                            />
                        </Form.Field>
                    </Form.Group>

                    <Divider id="divider-fatura" horizontal>
                        <Header as="h4">
                            <Icon name="file alternate outline" />
                        Fatura
                    </Header>
                    </Divider>

                    <Form.Group>
                    <Form.Field className="period">
                            <label>Data</label>
                            <MaskedInput
                                style={{ marginRight: 8 }}
                                className="masked-input"
                                mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                name="data_fatura_ini"
                                showMask={true}
                                value={filter.data_fatura_ini}
                                onChange={onFieldChange}
                            />
                            <MaskedInput
                                className="masked-input"
                                mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                name="data_fatura_fim"
                                showMask={true}
                                value={filter.data_fatura_fim}
                                onChange={onFieldChange}
                            />
                        </Form.Field>
                        <Form.Input name="numero_fatura" label='Número' value={filter.numero_fatura} onChange={onFieldChange} />
                    </Form.Group>

                    <Form.Group>
                        <Form.Field>
                            <label>Valor</label>
                            <Input
                                name="valor_fatura"
                                value={filter.valor_fatura}
                                onChange={onFieldChange}
                                label={<Dropdown defaultValue='=' options={[
                                    { key: '=', text: '=', value: '=' },
                                    { key: '>', text: '>', value: '>' },
                                    { key: '<', text: '<', value: '<' },
                                ]} />}
                                labelPosition='left'
                            />
                        </Form.Field>
                    </Form.Group>
                    <Form.Button
                        content="Filtrar"
                        icon="filter"
                        primary
                    />
                </Form>
            </Modal.Content>
        </Modal>
    )
}
